'use client';

import { useState, FormEvent, useRef } from 'react';
import Link from 'next/link';
import ScrollAnimate from '../ScrollAnimate';
import { reportLeadFormConversion } from '@/lib/gtag';

type InquiryMode = 'farm' | 'partnership' | 'general';

const inquiryModes: Array<{ value: InquiryMode; label: string }> = [
    { value: 'farm', label: 'Farm irrigation' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'general', label: 'General inquiry' },
];

const modeContent: Record<InquiryMode, { heading: string; description: string }> = {
    farm: {
        heading: 'Tell us about your farm',
        description: 'Share what you know — rough details are completely fine.',
    },
    partnership: {
        heading: 'Explore a partnership',
        description: 'Tell us briefly what you have in mind.',
    },
    general: {
        heading: 'How can we help?',
        description: 'Send us a short note and we’ll point it to the right person.',
    },
};

export default function ContactForm() {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [inquiryMode, setInquiryMode] = useState<InquiryMode>('farm');
    const formLoadTime = useRef<number>(Date.now());

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        const form = e.currentTarget;
        const formData = new FormData(form);
        
        const data = {
            inquiryType: formData.get('inquiryType'),
            firstName: formData.get('firstName'),
            lastName: formData.get('lastName'),
            email: formData.get('email'),
            phone: formData.get('phone'),
            organization: formData.get('organization'),
            role: formData.get('role'),
            location: formData.get('location'),
            size: formData.get('size'),
            interest: formData.get('interest'),
            message: formData.get('message'),
            // Honeypot field - should be empty
            website: formData.get('website'),
            // Timestamp to detect bot submissions
            formLoadTime: formLoadTime.current,
        };

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            // Check if response is JSON before parsing
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const text = await response.text();
                console.error('Non-JSON response received:', text.substring(0, 200));
                throw new Error('Server returned an invalid response. Please check the console for details.');
            }

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Failed to send message');
            }

            // Only past this point is the lead real: the API returned 200, so it
            // cleared the honeypot and timing checks and the email was accepted.
            reportLeadFormConversion();

            setIsSubmitting(false);
            setIsSubmitted(true);
            form.reset();
            form.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (err) {
            console.error('Error submitting form:', err);
            setError(err instanceof Error ? err.message : 'Failed to send message. Please try again.');
            setIsSubmitting(false);
        }
    };

    if (isSubmitted) {
        return (
            <ScrollAnimate delay={300}>
                <div className="max-w-xl mx-auto">
                    <div className="p-8 rounded-2xl bg-gradient-to-br from-[#10b981]/10 to-[#10b981]/5 border border-[#10b981]/20 dark:from-[#10b981]/20 dark:to-transparent dark:border-[#10b981]/30 text-center">
                        <div className="mb-4">
                            <svg
                                className="w-16 h-16 mx-auto text-[#10b981]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-3">
                            Message sent successfully!
                        </h2>
                        <p className="text-slate-300 mb-6">
                            Thank you for reaching out. We&apos;ll get back to you as soon as possible.
                        </p>
                        <button
                            onClick={() => {
                                setInquiryMode('farm');
                                setIsSubmitted(false);
                            }}
                            className="px-6 py-3 rounded-full bg-[#257676] text-white font-semibold hover:bg-[#1e5e5e] transition-all duration-300"
                        >
                            Send another message
                        </button>
                    </div>

                    <ScrollAnimate delay={400}>
                        <div className="mt-12 mb-12 text-center">
                            <p className="text-slate-400 mb-2">or email us</p>
                            <a
                                href="mailto:info@esci.io"
                                className="text-lg font-semibold text-[#7fd4cd] transition-colors hover:text-white"
                            >
                                info@esci.io
                            </a>
                        </div>
                    </ScrollAnimate>
                </div>
            </ScrollAnimate>
        );
    }

    return (
        <ScrollAnimate delay={300}>
            <div className="mx-auto max-w-xl">
                <div className="mb-4 text-center">
                    <h2 className="text-2xl font-semibold text-white">
                        Get in touch
                    </h2>
                    {/* <p className="mt-2 text-sm leading-relaxed text-slate-300">
                        Choose what you&apos;d like to talk about.
                    </p> */}
                </div>

                <form
                    className="space-y-3"
                    onSubmit={handleSubmit}
                >
                    {error && (
                        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 dark:bg-red-950/40 dark:border-red-900 dark:text-red-300">
                            <p className="text-sm font-medium">{error}</p>
                        </div>
                    )}

                    <fieldset>
                        <legend className="sr-only">Inquiry type</legend>
                        <div className="grid grid-cols-3 rounded-xl border border-slate-700 bg-[#17231f] p-1">
                            {inquiryModes.map((mode) => (
                                <label key={mode.value} className="relative cursor-pointer">
                                    <input
                                        type="radio"
                                        name="inquiryType"
                                        value={mode.value}
                                        checked={inquiryMode === mode.value}
                                        onChange={() => {
                                            setInquiryMode(mode.value);
                                            setError(null);
                                        }}
                                        className="peer sr-only"
                                    />
                                    <span className="flex min-h-10 items-center justify-center rounded-lg px-2 py-2 text-center text-xs font-semibold leading-tight text-slate-300 transition-colors peer-checked:bg-[#257676] peer-checked:text-white peer-focus-visible:ring-2 peer-focus-visible:ring-[#7fd4cd] peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[#17231f] sm:text-sm">
                                        {mode.label}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </fieldset>

                    <div className="py-1 text-center" aria-live="polite">
                        <h3 className="text-lg font-semibold text-white">
                            {modeContent[inquiryMode].heading}
                        </h3>
                        <p className="mt-1 text-sm leading-relaxed text-slate-300">
                            {modeContent[inquiryMode].description}
                        </p>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                        <div>
                            <label className="block text-sm font-medium text-slate-200">
                                Name
                            </label>
                            <input
                                name="firstName"
                                autoComplete="name"
                                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[#257676] focus:outline-none focus:ring-1 focus:ring-[#257676] dark:border-slate-700 dark:bg-[#1f2b27] dark:text-slate-100 dark:placeholder-slate-500"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-200">
                                Email
                            </label>
                            <input
                                type="email"
                                name="email"
                                autoComplete="email"
                                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[#257676] focus:outline-none focus:ring-1 focus:ring-[#257676] dark:border-slate-700 dark:bg-[#1f2b27] dark:text-slate-100 dark:placeholder-slate-500"
                                required
                            />
                        </div>
                    </div>

                    {inquiryMode === 'farm' && (
                        <div className="grid gap-3 md:grid-cols-2">
                            <div>
                                <label className="block text-sm font-medium text-slate-200">
                                    Farm or organization
                                </label>
                                <input
                                    type="text"
                                    name="organization"
                                    autoComplete="organization"
                                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[#257676] focus:outline-none focus:ring-1 focus:ring-[#257676] dark:border-slate-700 dark:bg-[#1f2b27] dark:text-slate-100 dark:placeholder-slate-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-200">
                                    Farm location
                                </label>
                                <input
                                    name="location"
                                    autoComplete="address-level2"
                                    placeholder="City, province or state"
                                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[#257676] focus:outline-none focus:ring-1 focus:ring-[#257676] dark:border-slate-700 dark:bg-[#1f2b27] dark:text-slate-100 dark:placeholder-slate-500"
                                    required
                                />
                            </div>
                        </div>
                    )}

                    {inquiryMode === 'partnership' && (
                        <div className="grid gap-3 md:grid-cols-2">
                            <div>
                                <label className="block text-sm font-medium text-slate-200">
                                    Organization
                                </label>
                                <input
                                    type="text"
                                    name="organization"
                                    autoComplete="organization"
                                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[#257676] focus:outline-none focus:ring-1 focus:ring-[#257676] dark:border-slate-700 dark:bg-[#1f2b27] dark:text-slate-100 dark:placeholder-slate-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-200">
                                    Your role <span className="font-normal text-slate-400">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    name="role"
                                    autoComplete="organization-title"
                                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[#257676] focus:outline-none focus:ring-1 focus:ring-[#257676] dark:border-slate-700 dark:bg-[#1f2b27] dark:text-slate-100 dark:placeholder-slate-500"
                                />
                            </div>
                        </div>
                    )}

                    {inquiryMode === 'general' && (
                        <div>
                            <label className="block text-sm font-medium text-slate-200">
                                Subject <span className="font-normal text-slate-400">(optional)</span>
                            </label>
                            <input
                                type="text"
                                name="interest"
                                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[#257676] focus:outline-none focus:ring-1 focus:ring-[#257676] dark:border-slate-700 dark:bg-[#1f2b27] dark:text-slate-100 dark:placeholder-slate-500"
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-slate-200">
                            {inquiryMode === 'farm'
                                ? 'Tell us about your irrigation'
                                : inquiryMode === 'partnership'
                                  ? 'What would you like to explore?'
                                  : 'Message'}
                        </label>
                        <textarea
                            name="message"
                            rows={2}
                            placeholder={
                                inquiryMode === 'farm'
                                    ? 'Fields, valves, existing wiring, or what you would like to improve'
                                    : inquiryMode === 'partnership'
                                      ? 'A few details about the opportunity'
                                      : 'How can we help?'
                            }
                            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[#257676] focus:outline-none focus:ring-1 focus:ring-[#257676] dark:border-slate-700 dark:bg-[#1f2b27] dark:text-slate-100 dark:placeholder-slate-500"
                            required
                        />
                    </div>

                    {/* Honeypot field - hidden from users but visible to bots */}
                    <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                        <label htmlFor="website">Website</label>
                        <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" />
                    </div>

                    <p className="text-xs leading-relaxed text-slate-300">
                        We&apos;ll only use these details to respond.{' '}
                        <Link
                            href="/privacy"
                            className="underline underline-offset-2 hover:text-[#7fd4cd]"
                        >
                            Privacy policy
                        </Link>
                    </p>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full rounded-full bg-[#257676] px-8 py-4 text-lg font-semibold text-white shadow-lg hover:bg-[#1e5e5e] transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                        {isSubmitting ? 'Sending...' : 'Start the conversation'}
                    </button>
                </form>

                <ScrollAnimate delay={400}>
                    <div className="mb-4 mt-7 text-center">
                        <p className="text-slate-400 mb-2">or email us</p>
                        <a
                            href="mailto:info@esci.io"
                            className="text-lg font-semibold text-[#7fd4cd] transition-colors hover:text-white"
                        >
                            info@esci.io
                        </a>
                    </div>
                </ScrollAnimate>
            </div>
        </ScrollAnimate>
    );
}
